import React, { useState, useMemo } from 'react';
import ButtonBasic from '../../../../atoms/buttonBasic/view';
import { decodeBase64Utf8, parseXmlData } from '../../utils/xml-parser';
import { formatMoney } from '../../../../../utils/formating';
import './style.css';

const XmlPreview = ({ base64Data, extractedData }) => {
  const [showRaw, setShowRaw] = useState(false);

  const xmlText = useMemo(() => decodeBase64Utf8(base64Data), [base64Data]);
  const parsedData = useMemo(() => {
    const rawParsed = parseXmlData(xmlText);
    if (!extractedData) return rawParsed;
    return {
      ...rawParsed,
      success: rawParsed.success || true,
      id: rawParsed.id || extractedData.documento_compra,
      issueDate: rawParsed.issueDate || (extractedData.buy_date ? extractedData.buy_date.substring(0, 10) : ''),
      supplierName: rawParsed.supplierName || extractedData.tercero?.name,
      supplierId: rawParsed.supplierId || extractedData.tercero?.identification,
      customerName: rawParsed.customerName || extractedData.adquirente?.name,
      customerId: rawParsed.customerId || extractedData.adquirente?.identification,
      payableAmount: rawParsed.payableAmount || extractedData.total,
      lineExtensionAmount: rawParsed.lineExtensionAmount || extractedData.subtotal || extractedData.total,
      items: (rawParsed.items && rawParsed.items.length > 0) ? rawParsed.items : (extractedData.items || [])
    };
  }, [xmlText, extractedData]);


  return (
    <div className="xml-preview-container">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0 xml-preview-title">Vista Previa XML</h5>
        <ButtonBasic
          type="cancel"
          onClick={() => setShowRaw(!showRaw)}
          title={showRaw ? 'Ver Resumen Estructurado' : 'Ver XML Origen'}
        />
      </div>

      {showRaw ? (
        <div style={{ flex: 1, minHeight: '300px' }}>
          <pre style={{
            backgroundColor: '#272822',
            color: '#f8f8f2',
            padding: '12px',
            borderRadius: '8px',
            maxHeight: '60vh',
            overflow: 'auto',
            fontSize: '0.8rem',
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all'
          }}>
            {xmlText}
          </pre>
        </div>
      ) : (
        parsedData.success ? (
          <div className="xml-parsed-content d-flex flex-column gap-3">
            <div className="xml-preview-card">
              <div className="row g-2">
                <div className="col-6">
                  <div className="xml-preview-label">Nro. Documento</div>
                  <strong className="xml-preview-highlight-value">{parsedData.id || 'N/A'}</strong>
                </div>
                <div className="col-6">
                  <div className="xml-preview-label">Fecha Emisión</div>
                  <strong className="xml-preview-value">{parsedData.issueDate} {parsedData.issueTime ? `(${parsedData.issueTime.substring(0, 5)})` : ''}</strong>
                </div>
              </div>
            </div>

            <div className="row g-2">
              <div className="col-md-6">
                <div className="xml-preview-card h-100">
                  <div className="xml-preview-label mb-1 fw-bold">Emisor / Proveedor</div>
                  <div className="xml-preview-value" style={{ fontWeight: 600 }}>{parsedData.supplierName || 'N/A'}</div>
                  <div className="xml-preview-label">NIT: {parsedData.supplierId || 'N/A'}</div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="xml-preview-card h-100">
                  <div className="xml-preview-label mb-1 fw-bold">Adquirente / Cliente</div>
                  <div className="xml-preview-value" style={{ fontWeight: 600 }}>{parsedData.customerName || 'N/A'}</div>
                  <div className="xml-preview-label">NIT: {parsedData.customerId || 'N/A'}</div>
                </div>
              </div>
            </div>

            {parsedData.items?.length > 0 && (
              <div className="xml-preview-card">
                <div className="xml-preview-label mb-2 fw-bold">Detalle de Productos/Servicios</div>
                <div className="xml-preview-table-container">
                  <table className="xml-preview-table">
                    <thead>
                      <tr>
                        <th>Descripción</th>
                        <th className="text-end">Cant.</th>
                        <th className="text-end">Precio U.</th>
                        <th className="text-end">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedData.items.map((item, index) => (
                        <tr key={index}>
                          <td>
                            <div className="xml-preview-value">{item.name}</div>
                            {item.sku && <span className="xml-preview-label" style={{ fontSize: '0.75rem' }}>SKU: {item.sku}</span>}
                          </td>
                          <td className="text-end xml-preview-value">{item.quantity}</td>
                          <td className="text-end xml-preview-value">{formatMoney(Number(item.price))}</td>
                          <td className="text-end xml-preview-value">{formatMoney(Number(item.total))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="xml-preview-totals">
              <div className="xml-preview-total-row">
                <span>Subtotal Neto:</span>
                <span>{formatMoney(Number(parsedData.lineExtensionAmount || parsedData.taxExclusiveAmount || 0))}</span>
              </div>
              {Number(parsedData.taxInclusiveAmount || 0) > Number(parsedData.taxExclusiveAmount || 0) && (
                <div className="xml-preview-total-row">
                  <span>Impuestos:</span>
                  <span>{formatMoney(Number(parsedData.taxInclusiveAmount) - Number(parsedData.taxExclusiveAmount || parsedData.lineExtensionAmount))}</span>
                </div>
              )}
              <div className="xml-preview-total-final">
                <span>Total Factura:</span>
                <span>{formatMoney(Number(parsedData.payableAmount || parsedData.taxInclusiveAmount || 0))}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="xml-preview-empty-state">
            No se pudo estructurar el XML. Utilice la vista de XML Origen para inspeccionar el contenido.
          </div>
        )
      )}
    </div>
  );
};

export default XmlPreview;
